// ─── UTILIDADES DE EXPORTACIÓN ───────────────────────────────
// PDF via ventana de impresión del navegador
// Excel via SheetJS (.xlsx nativo)

import * as XLSX from 'xlsx';

const formatCOP = (v) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

// ─── UTILIDAD EXCEL (.xlsx) ───────────────────────────────────

const descargarExcel = (nombre, encabezado, filas) => {
  const datos = [encabezado, ...filas];
  const ws = XLSX.utils.aoa_to_sheet(datos);
  const anchos = encabezado.map((col, i) => ({
    wch: Math.max(col.length, ...filas.map(f => String(f[i] || '').length), 10)
  }));
  ws['!cols'] = anchos;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Datos');
  XLSX.writeFile(wb, `${nombre}.xlsx`);
};

// ─── PDF ─────────────────────────────────────────────────────

const estilosPDF = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, sans-serif; font-size: 12px; color: #1a1a2e; padding: 24px; }
  h1 { font-size: 20px; color: #1a1a2e; margin-bottom: 4px; }
  h2 { font-size: 14px; color: #4a4a6a; margin: 20px 0 10px; border-bottom: 2px solid #e5e7eb; padding-bottom: 6px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; border-bottom: 3px solid #1a1a2e; padding-bottom: 12px; }
  .empresa { font-size: 22px; font-weight: 800; color: #1a1a2e; }
  .periodo { font-size: 12px; color: #6b7280; text-align: right; }
  .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }
  .kpi { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; text-align: center; }
  .kpi-val { font-size: 18px; font-weight: 700; color: #1a1a2e; }
  .kpi-label { font-size: 10px; color: #6b7280; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
  th { background: #f3f4f6; padding: 8px 10px; text-align: left; font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
  td { padding: 8px 10px; border-bottom: 1px solid #f3f4f6; font-size: 12px; }
  tr:nth-child(even) td { background: #fafafa; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 20px; font-size: 10px; font-weight: 600; }
  .badge-verde { background: #d1fae5; color: #065f46; }
  .badge-amarillo { background: #fef3c7; color: #92400e; }
  .badge-rojo { background: #fee2e2; color: #991b1b; }
  .total-row td { font-weight: 700; border-top: 2px solid #e5e7eb; background: #f9fafb; }
  .pie { text-align: center; color: #9ca3af; font-size: 10px; margin-top: 24px; border-top: 1px solid #e5e7eb; padding-top: 10px; }
  @media print { body { padding: 12px; } @page { margin: 1cm; } }
`;

const abrirVentanaPDF = (titulo, contenido, periodo, empresa = {}) => {
  const logoHtml = empresa.logo_base64
    ? `<img src="data:${empresa.logo_mime||'image/png'};base64,${empresa.logo_base64}" style="height:50px;object-fit:contain;margin-right:12px;" />`
    : '';
  const datosEmpresa = `
    <div style="display:flex;align-items:center;margin-bottom:4px;">
      ${logoHtml}
      <div>
        <div style="font-size:20px;font-weight:800;color:#1a1a2e;">${empresa.nombre || 'TELECOM'}</div>
        ${empresa.nit ? `<div style="font-size:11px;color:#6b7280;">NIT: ${empresa.nit}</div>` : ''}
      </div>
    </div>
    ${empresa.telefono ? `<div style="font-size:11px;color:#6b7280;">Tel: ${empresa.telefono}${empresa.telefono2 ? ` / ${empresa.telefono2}` : ''}</div>` : ''}
    ${empresa.direccion ? `<div style="font-size:11px;color:#6b7280;">${empresa.direccion}${empresa.ciudad ? `, ${empresa.ciudad}` : ''}</div>` : ''}
    ${empresa.email ? `<div style="font-size:11px;color:#6b7280;">${empresa.email}</div>` : ''}
  `;
  const ventana = window.open('', '_blank');
  ventana.document.write(`
    <!DOCTYPE html><html><head>
    <meta charset="utf-8">
    <title>${titulo} — ${periodo}</title>
    <style>${estilosPDF}</style>
    </head><body>
    <div class="header">
      <div>${datosEmpresa}</div>
      <div class="periodo">
        <div style="font-size:16px;font-weight:700;">${titulo}</div>
        <div>${periodo}</div>
        <div>Generado: ${new Date().toLocaleDateString('es-CO', { day:'2-digit', month:'long', year:'numeric' })}</div>
      </div>
    </div>
    ${contenido}
    <div class="pie">${empresa.pie_factura || '¡Gracias por su preferencia!'} · Sistema de Gestión ${empresa.nombre || 'Telecom'}</div>
    </body></html>
  `);
  ventana.document.close();
  setTimeout(() => ventana.print(), 500);
};

// ─── EXPORTAR CARTERA ─────────────────────────────────────────

export const exportarCarteraPDF = (cobros, resumen, mes, anio, empresa = {}) => {
  const periodo = `${MESES[mes-1]} ${anio}`;
  const filas = cobros.map(c => `
    <tr>
      <td>${c.cliente_nombre}</td>
      <td>${c.plan_nombre || '—'}</td>
      <td>${formatCOP(c.monto)}</td>
      <td>Día ${c.dia_pago}</td>
      <td><span class="badge ${c.estado==='pagado'?'badge-verde':c.estado==='vencido'?'badge-rojo':'badge-amarillo'}">${c.estado}</span></td>
      <td>${c.fecha_pago ? new Date(c.fecha_pago).toLocaleDateString('es-CO') : '—'}</td>
      ${c.descuento > 0 ? `<td>-${formatCOP(c.descuento)}</td>` : '<td>—</td>'}
    </tr>
  `).join('');

  const contenido = `
    <div class="kpis">
      <div class="kpi"><div class="kpi-val">${resumen.total || 0}</div><div class="kpi-label">Total cobros</div></div>
      <div class="kpi"><div class="kpi-val" style="color:#065f46">${formatCOP(resumen.monto_cobrado)}</div><div class="kpi-label">Cobrado</div></div>
      <div class="kpi"><div class="kpi-val" style="color:#92400e">${formatCOP(resumen.monto_pendiente)}</div><div class="kpi-label">Pendiente</div></div>
      <div class="kpi"><div class="kpi-val" style="color:#991b1b">${resumen.vencidos || 0}</div><div class="kpi-label">Vencidos</div></div>
    </div>
    <h2>Detalle de cobros — ${periodo}</h2>
    <table>
      <thead><tr><th>Cliente</th><th>Plan</th><th>Monto</th><th>Día pago</th><th>Estado</th><th>Fecha pago</th><th>Descuento</th></tr></thead>
      <tbody>${filas}</tbody>
      <tr class="total-row">
        <td colspan="2">TOTALES</td>
        <td>${formatCOP(cobros.reduce((s,c)=>s+parseFloat(c.monto),0))}</td>
        <td colspan="3"></td>
        <td>${formatCOP(cobros.reduce((s,c)=>s+parseFloat(c.descuento||0),0))}</td>
      </tr>
    </table>
  `;
  abrirVentanaPDF(`Reporte de Cartera`, contenido, periodo, empresa);
};

export const exportarCarteraExcel = (cobros, mes, anio) => {
  const periodo = `${MESES[mes-1]}_${anio}`;
  const encabezado = ['Cliente','Cédula','Teléfono','Plan','Monto','Día Pago','Estado','Fecha Pago','Descuento','Motivo Descuento'];
  const filas = cobros.map(c => [
    c.cliente_nombre, c.cedula || '', c.telefono || '',
    c.plan_nombre || '', c.monto, c.dia_pago, c.estado,
    c.fecha_pago ? new Date(c.fecha_pago).toLocaleDateString('es-CO') : '',
    c.descuento || 0, c.motivo_descuento || ''
  ]);
  descargarExcel(`Cartera_${periodo}`, encabezado, filas);
};

// ─── EXPORTAR CLIENTES ────────────────────────────────────────

export const exportarClientesPDF = (clientes, empresa = {}) => {
  const filas = clientes.map(c => `
    <tr>
      <td>${c.nombre}</td>
      <td>${c.cedula || '—'}</td>
      <td>${c.telefono || '—'}</td>
      <td>${c.email || '—'}</td>
      <td>${c.plan_nombre || '—'}</td>
      <td><span class="badge ${c.estado==='activo'?'badge-verde':c.estado==='moroso'?'badge-rojo':'badge-amarillo'}">${c.estado}</span></td>
      <td>${c.fecha_instalacion ? new Date(c.fecha_instalacion).toLocaleDateString('es-CO') : '—'}</td>
      <td>${c.direccion || '—'}</td>
    </tr>
  `).join('');

  const activos = clientes.filter(c=>c.estado==='activo').length;
  const morosos = clientes.filter(c=>c.estado==='moroso').length;

  const contenido = `
    <div class="kpis">
      <div class="kpi"><div class="kpi-val">${clientes.length}</div><div class="kpi-label">Total clientes</div></div>
      <div class="kpi"><div class="kpi-val" style="color:#065f46">${activos}</div><div class="kpi-label">Activos</div></div>
      <div class="kpi"><div class="kpi-val" style="color:#991b1b">${morosos}</div><div class="kpi-label">Morosos</div></div>
      <div class="kpi"><div class="kpi-val">${clientes.length - activos - morosos}</div><div class="kpi-label">Otros</div></div>
    </div>
    <h2>Listado de clientes</h2>
    <table>
      <thead><tr><th>Nombre</th><th>Cédula</th><th>Teléfono</th><th>Email</th><th>Plan</th><th>Estado</th><th>F. Instalación</th><th>Dirección</th></tr></thead>
      <tbody>${filas}</tbody>
    </table>
  `;
  abrirVentanaPDF('Listado de Clientes', contenido, new Date().toLocaleDateString('es-CO'), empresa);
};

export const exportarClientesExcel = (clientes) => {
  const encabezado = ['Nombre','Cédula','Teléfono','Email','Dirección','Plan','Precio Mensual','Estado','Día Pago','Fecha Instalación','Notas'];
  const filas = clientes.map(c => [
    c.nombre, c.cedula || '', c.telefono || '', c.email || '',
    c.direccion || '', c.plan_nombre || '', c.precio_mensual || 0,
    c.estado, c.dia_pago,
    c.fecha_instalacion ? new Date(c.fecha_instalacion).toLocaleDateString('es-CO') : '',
    c.notas || ''
  ]);
  descargarExcel('Clientes', encabezado, filas);
};

// ─── EXPORTAR VENTAS ──────────────────────────────────────────

export const exportarVentasPDF = (ventas, resumen, mes, anio, empresa = {}) => {
  const periodo = `${MESES[mes-1]} ${anio}`;
  const filas = ventas.map(v => `
    <tr>
      <td>${v.numero_recibo}</td>
      <td>${new Date(v.fecha).toLocaleDateString('es-CO')}</td>
      <td>${v.cliente_nombre || v.cliente_nombre_reg || '—'}</td>
      <td>${v.num_items} ítem(s)</td>
      <td>${formatCOP(v.subtotal)}</td>
      <td>${v.descuento > 0 ? `-${formatCOP(v.descuento)}` : '—'}</td>
      <td><strong>${formatCOP(v.total)}</strong></td>
      <td>${v.metodo_pago}</td>
    </tr>
  `).join('');

  const contenido = `
    <div class="kpis">
      <div class="kpi"><div class="kpi-val">${resumen.total_ventas || 0}</div><div class="kpi-label">Número de ventas</div></div>
      <div class="kpi"><div class="kpi-val" style="color:#065f46">${formatCOP(resumen.total_ingresos)}</div><div class="kpi-label">Total ingresos</div></div>
      <div class="kpi"><div class="kpi-val" style="color:#4338ca">${formatCOP(resumen.total_productos)}</div><div class="kpi-label">Productos</div></div>
      <div class="kpi"><div class="kpi-val" style="color:#0891b2">${formatCOP(resumen.total_servicios)}</div><div class="kpi-label">Servicios</div></div>
    </div>
    <h2>Detalle de ventas — ${periodo}</h2>
    <table>
      <thead><tr><th>N° Recibo</th><th>Fecha</th><th>Cliente</th><th>Ítems</th><th>Subtotal</th><th>Descuento</th><th>Total</th><th>Método</th></tr></thead>
      <tbody>${filas}</tbody>
      <tr class="total-row">
        <td colspan="4">TOTAL</td>
        <td>${formatCOP(ventas.reduce((s,v)=>s+parseFloat(v.subtotal||0),0))}</td>
        <td>${formatCOP(ventas.reduce((s,v)=>s+parseFloat(v.descuento||0),0))}</td>
        <td>${formatCOP(ventas.reduce((s,v)=>s+parseFloat(v.total||0),0))}</td>
        <td></td>
      </tr>
    </table>
  `;
  abrirVentanaPDF('Reporte de Ventas y Servicios', contenido, periodo, empresa);
};

export const exportarVentasExcel = (ventas, mes, anio) => {
  const periodo = `${MESES[mes-1]}_${anio}`;
  const encabezado = ['N° Recibo','Fecha','Cliente','N° Ítems','Subtotal','Descuento','Total','Método Pago','Notas'];
  const filas = ventas.map(v => [
    v.numero_recibo,
    new Date(v.fecha).toLocaleDateString('es-CO'),
    v.cliente_nombre || v.cliente_nombre_reg || '',
    v.num_items, v.subtotal, v.descuento || 0, v.total,
    v.metodo_pago, v.notas || ''
  ]);
  descargarExcel(`Ventas_${periodo}`, encabezado, filas);
};
